import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Paper,
  Card,
  CardContent,
} from '@mui/material';
import { getPageBySlug, getTemplate, CMSPage, CMSSection, CMSTemplate } from '../firebase/services/cmsService';

const CMSPageRenderer: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<CMSPage | null>(null);
  const [template, setTemplate] = useState<CMSTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const loadPage = async () => {
      if (!slug) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setNotFound(false);
        
        const pageData = await getPageBySlug(slug);
        
        if (pageData && pageData.status === 'published') {
          setPage(pageData);
          
          // Load the template if it exists
          if (pageData.template) {
            try {
              const templateData = await getTemplate(pageData.template);
              setTemplate(templateData);
            } catch (error) {
              console.error('Error loading template:', error);
              // Page can still render without template
            }
          }
        } else {
          setNotFound(true);
        }
      } catch (error) {
        console.error('Error loading CMS page:', error);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    loadPage();
  }, [slug]);

  const renderSection = (section: CMSSection, layoutOptions?: { containerMaxWidth: any; spacing: number }) => {
    if (!section.isVisible) return null;
    
    const maxWidth = layoutOptions?.containerMaxWidth || 'lg';
    const sectionSpacing = layoutOptions?.spacing || 4;

    const sectionContent = (
      <Box key={section.id} sx={{ mb: 4 }}>
        {section.title && (
          <Typography 
            variant="h4" 
            component="h2" 
            gutterBottom 
            sx={{ fontWeight: 'bold', mb: 3 }}
          >
            {section.title}
          </Typography>
        )}
        
        {section.content && (
          <Typography 
            component="div"
            sx={{ 
              '& p': { mb: 2 },
              '& h1, & h2, & h3, & h4, & h5, & h6': { 
                fontWeight: 'bold',
                mt: 3,
                mb: 2,
              },
              '& ul, & ol': { 
                pl: 3,
                mb: 2,
              },
              '& img': {
                maxWidth: '100%',
                height: 'auto',
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
            }}
            dangerouslySetInnerHTML={{ __html: section.content }}
          />
        )}
      </Box>
    );

    // Render different section types with appropriate layouts
    switch (section.type) {
      case 'hero':
        return (
          <Box
            key={section.id}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              py: 8,
              mb: sectionSpacing,
              textAlign: 'center',
            }}
          >
            <Container maxWidth={maxWidth}>
              {sectionContent}
            </Container>
          </Box>
        );

      case 'image_text':
        return (
          <Container key={section.id} maxWidth={maxWidth} sx={{ mb: sectionSpacing }}>
            <Card>
              <CardContent sx={{ p: 4 }}>
                {sectionContent}
              </CardContent>
            </Card>
          </Container>
        );

      case 'services':
        return (
          <Box key={section.id} sx={{ bgcolor: 'grey.50', py: 6, mb: sectionSpacing }}>
            <Container maxWidth={maxWidth}>
              {sectionContent}
            </Container>
          </Box>
        );

      case 'contact_info':
        return (
          <Container key={section.id} maxWidth={maxWidth} sx={{ mb: sectionSpacing }}>
            <Paper sx={{ p: 4 }}>
              {sectionContent}
            </Paper>
          </Container>
        );

      default:
        return (
          <Container key={section.id} maxWidth={maxWidth} sx={{ mb: sectionSpacing }}>
            {sectionContent}
          </Container>
        );
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '60vh' 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (notFound) {
    // Return null to let the 404 route handle it
    return <Navigate to="/404" replace />;
  }

  if (!page) {
    return <Navigate to="/404" replace />;
  }

  const containerMaxWidth = template?.layoutSettings?.containerMaxWidth || 'lg';
  const spacing = template?.layoutSettings?.spacing || 4;
  const backgroundColor = template?.layoutSettings?.backgroundColor || '#ffffff';
  const showHeader = template?.layoutSettings?.headerSettings?.showHeader !== false;
  const showFooter = template?.layoutSettings?.footerSettings?.showFooter !== false;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: backgroundColor }}>
      {/* Page Header */}
      {showHeader && (
        <Box sx={{ py: 4, mb: 4, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Container maxWidth={containerMaxWidth}>
            <Typography 
              variant="h1" 
              component="h1" 
              sx={{ 
                fontWeight: 'bold',
                textAlign: 'center',
                mb: 2
              }}
            >
              {page.title}
            </Typography>
            
            {page.metaDescription && (
              <Typography 
                variant="h6" 
                color="text.secondary" 
                sx={{ textAlign: 'center' }}
              >
                {page.metaDescription}
              </Typography>
            )}
          </Container>
        </Box>
      )}

      {/* Page Content */}
      <Box sx={{ pb: 6 }}>
        {page.sections
          ?.sort((a, b) => a.order - b.order)
          .map(section => renderSection(section, { containerMaxWidth, spacing }))
        }
      </Box>

      {/* Footer spacer */}
      {showFooter && <Box sx={{ py: 4 }} />}
    </Box>
  );
};

export default CMSPageRenderer; 